<script setup>
import { ref, computed } from 'vue';
import { useQuery } from 'villus';
import map from 'lodash-es/map';
import DiscountsListingCards from '@/components/discounts-listing/DiscountsListingCards.vue';
import { useStore } from '@/store/Store.js';
import Wave from '@/icons/wave.svg';
import SentierEconomiesLarge from '@/icons/sentier-economies-large.svg';
import { getClient } from '@/core/js/GraphqlClient.js';
import WaveCurve from '@/icons/wave-curve.svg';
import wysiwyg from '@/components/wysiwyg/Wysiwyg.vue';

const client = getClient();

const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },
    color: {
        type: String,
        default: '',
    },
    themeColor: {
        type: String,
        default: '',
    },
});

const store = useStore();
const variables = computed(() => ({ lang: store.lang.value }));
const groceries = ref([]);
const ingredientIds = ref([]);
const recipesQuantities = ref({});

let discounts = props.data.data.selectedDiscounts;

if (!props.data.data || !props.data.data.selectedDiscounts || props.data.data.selectedDiscounts.length == 0) {
    const { data: queryData } = await useQuery({
        variables,
        query: `
            query FetchSentier($lang: [String]) {
                discounts: entries (section: "discount", site: $lang, limit: 4, orderBy: "RAND()") {
                    ... on discount_Entry {
                        title,
                        status,
                        prize: discountPrize,
                        unit: discountUnit,
                        url: discountUrl,
                        discountStatus: discountStatus,
                        ingredients: discountIngredients { id, slug, title },
                        grocery: discountGrocery {
                            ... on superStore_Entry {
                                id,
                                postDate,
                                title,
                                slug,
                                logo: groceryLogo {
                                    url @transform(format: "webp", quality: 80, width: 500, immediately: true)
                                }
                            },
                            ... on saleSpecialist_Entry {
                                id,
                                postDate,
                                title,
                                slug,
                                logo: groceryLogo {
                                    url @transform(format: "webp", quality: 80, width: 500, immediately: true)
                                }
                            },
                            ... on specializedGrocery_Entry {
                                id,
                                postDate,
                                title,
                                slug,
                                logo: groceryLogo {
                                    url @transform(format: "webp", quality: 80, width: 500, immediately: true)
                                }
                            } 
                        }
                    }
                }
            }
        `,
    });

    discounts = queryData.value.discounts;
}

if (discounts && discounts.length > 0) {
    groceries.value = map(discounts, (disc) => disc.grocery[0]);
    groceries.value = Object.fromEntries(map(groceries.value, (groc) => [groc.id, groc]));

    /**
     * Get the count of recipes for each ingredient for further reference in the template
     */
    for (let i = 0, il = discounts.length; i < il; ++i) {
        ingredientIds.value = ingredientIds.value.concat(map(discounts[i].ingredients, (ing) => ing.id));
    }
    await Promise.all(
        ingredientIds.value.map(async (id) => {
            const { data: recipesCountData } = await client.executeQuery({
                variables: {
                    lang: store.lang.value,
                    recipesIngredients: id,
                },
                query: `query FetchEntries($lang: [String], $recipesIngredients: [QueryArgument])  {
                    recipesCount: entryCount(section: "recette", site: $lang, relatedTo: $recipesIngredients)
                }`,
            });
            recipesQuantities.value[id] = recipesCountData.recipesCount;
        }),
    );
}

const attrs = computed(() => ({
    posts: discounts,
    'title-top': props.data.data.featuredContentTitle,
    'cta-link': props.data.data.link?.[0]?.url ?? null,
    cta: props.data.data.link?.[0]?.text ?? null,
    groceries: groceries.value,
}));
</script>

<template>
    <div class="HomeDiscount Component relative my-0 justify-between bg-light-blue pb-10 pt-5 text-dark-blue md:flex-row">
        <div class="absolute -top-6 w-full sm:-top-10 md:-top-20">
            <WaveCurve class="block w-full translate-y-[-40%] fill-current text-light-blue md:hidden" />
            <Wave class="hidden fill-current text-light-blue md:block 3xl:w-full" />
        </div>
        <div class="absolute right-[3%] top-[2.3%] z-10 w-[40%] translate-y-[-93%] md:top-0 md:w-[508px] md:max-w-[30%] xl:max-w-[20%]">
            <SentierEconomiesLarge />
        </div>
        <div class="absolute right-[6%] top-0 z-0 hidden h-full md:block">
            <svg
                width="190"
                height="2081"
                viewBox="0 0 190 2081"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M17 0V604.595C17 691.574 44.2803 776.363 95 847.024V847.024C145.72 917.685 173 1002.47 173 1089.45V1437.95C173 1502.47 152.766 1565.36 115.146 1617.77V1617.77C77.5258 1670.18 57.2914 1733.07 57.2914 1797.58V2114"
                    stroke="#33567B"
                    stroke-opacity="0.3"
                    stroke-width="33"
                />
            </svg>
        </div>
        <div class="absolute bottom-18 right-[3%] z-10 hidden w-[335px] max-w-[15%] md:block">
            <svg
                width="335"
                height="135"
                viewBox="0 0 335 135"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M335 150.874H263.536C263.536 128.171 279.533 109.781 299.282 109.781C319.003 109.814 335 128.203 335 150.874Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M312.401 112.668C312.401 112.668 296.63 136.279 293.217 150.874"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M284.104 113.673C284.104 113.673 287.546 127.781 300.383 133.198"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M295.756 143.155C295.756 143.155 316.549 142.506 330.458 130.02"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.878353"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M187.785 170.691C187.785 170.691 165.864 46.0823 261.308 1C261.308 1 292.032 109.23 187.785 170.691Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M261.308 1C261.308 1 199.211 99.6621 189.111 172.475"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.849754"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M202.879 167.448C202.879 167.448 220.23 41.8988 323.348 38.0068C323.348 38.0393 319.229 151.75 202.879 167.448Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M202.879 167.448C202.879 167.448 278.433 62.1048 323.348 38.0068"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.849754"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M272.988 81.4023L273.834 50.0068"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.887682"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M243.562 115.004L245.593 72.8408"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.881901"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M282.439 71.543L317.677 72.7106"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.883618"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M253.916 102.744L303.937 104.139"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.88751"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M181.86 197.999C181.86 197.999 214.333 106.862 140.021 41.541C140.021 41.541 100.551 128.754 181.86 197.999Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M162.224 183.859C162.224 183.859 144.45 55.2606 38.8208 51.3037C38.8208 51.3037 43.0245 167.804 162.224 183.859Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M162.224 183.859C162.224 183.859 84.8361 75.9855 38.8208 51.3037"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.870359"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M80.0681 85.1968L79.1653 59.9961"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.909203"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M114.29 122.462L111.413 81.0771"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.903288"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M90.4504 95.6396L48.3004 98.0073"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="0.905043"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M31.1753 104.43C37.1846 104.43 42.799 106.376 47.567 109.782C55.7487 96.9057 68.9806 88.5703 83.877 88.5703C102.469 88.5703 118.41 101.544 125.265 120.031C129.469 115.685 134.999 113.057 141.093 113.057C154.184 113.057 164.792 125.252 164.792 140.301H128.848H117.394H62.3505H38.9056H0C0 120.485 13.9372 104.43 31.1753 104.43Z"
                    fill="#33567B"
                    fill-opacity="0.3"
                />
                <path
                    d="M92.1151 141.274C92.1151 141.274 85.0901 98.9165 80.3503 88.5703"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M90.3659 131.252L53.5198 102.127"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M109.664 96.873L87.5728 116.56"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M40.9087 141.274L13.0343 111.111"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.233"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M35.4636 103.749L28.1846 127.523"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.23243"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M136.325 140.269L150.995 114.42"
                    stroke="#33567B"
                    stroke-opacity="0.5"
                    stroke-width="1.23243"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>

        <div class="px-6 xl:px-20 relative">
            <template v-if="attrs.posts">
                <div class="HomeDiscounts__title mb-8 w-1/2 pr-8 md:mb-0 md:pr-0">
                    <h2 class="h2 mb-2 md:mb-10">
                        <span class="text-dark-blue">
                            <span>{{ attrs['title-top'] }}</span>
                        </span>
                    </h2>
                </div>

                <div
                    v-if="data.data.featuredContentText"
                    class="BlogFeaturedList__text mb-8 xl:mb-12"
                >
                    <wysiwyg
                        :center="false"
                        :space="false"
                        :color="color"
                        :data="{ data: { wysiwyg: data.data.featuredContentText } }"
                    />
                </div>
                <div class="HomeDiscounts__discounts md:max-w-[75%] md:basis-195">
                    <DiscountsListingCards
                        class="mb-16"
                        :display-recipes-cta="false"
                        :discounts="attrs.posts"
                        :groceries="attrs.groceries"
                        :display-grocery-title="true"
                        :recipes-quantities="recipesQuantities"
                    />
                </div>
                <div class="flex justify-center pb-16 text-center md:w-[75%]">
                    <router-link
                        class="HomeDiscounts__cta"
                        :to="attrs['cta-link']"
                    >
                        <Btn :text="attrs.cta" />
                    </router-link>
                </div>
            </template>
        </div>
    </div>
</template>
